import React from 'react';
import { Clock, AlertCircle, Building2, MapPin, Download, Trash2 } from 'lucide-react';
import { SavedOutput } from '@/api/types';
import { getCardBorderColor, isNeedsFollowup, handleDownload, columns } from './SavedUtils';

interface SavedBoardProps {
  outputs: SavedOutput[];
  onOpen: (output: SavedOutput) => void;
  onDelete: (id: string) => void;
  onUpdateOutput: (id: string, updates: Partial<SavedOutput>) => void;
}

export function SavedBoard({ outputs, onOpen, onDelete, onUpdateOutput }: SavedBoardProps) {
  return (
    <div className="flex-1 overflow-x-auto overflow-y-hidden no-scrollbar pb-4 min-h-0">
      <div className="flex gap-4 h-full min-w-max pb-2">
        {columns.map(statusColumn => {
          const colOutputs = outputs.filter(out => (out.status || 'To Apply') === statusColumn);
          return (
            <div 
              key={statusColumn} 
              className="w-80 flex flex-col h-full max-h-full overflow-hidden bg-slate-50 dark:bg-slate-900/30 rounded-xl border border-slate-200 dark:border-slate-800"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain');
                if (id) {
                  const out = outputs.find(o => o.id === id);
                  if (!out || (out.status || 'To Apply') === statusColumn) return;
                  const updates: Partial<SavedOutput> = { status: statusColumn as any };
                  if (statusColumn === 'Applied' && out.status !== 'Applied') {
                    updates.meta = { ...out.meta, appliedDate: new Date().toISOString() };
                  }
                  onUpdateOutput(id, updates);
                }
              }}
            >
              <div className="p-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-100/50 dark:bg-slate-800/30 shrink-0">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">{statusColumn}</h3>
                <span className="text-xs font-medium text-slate-500 bg-slate-200 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                  {colOutputs.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2 no-scrollbar">
                {colOutputs.map(out => {
                  const td = out.meta?.targetDetails;
                  const needsFollowup = isNeedsFollowup(out);
                  
                  return (
                    <div 
                      key={out.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', out.id);
                      }}
                      className={`bg-white dark:bg-slate-950 p-3 rounded-lg border ${getCardBorderColor(out.status)} dark:shadow-sm cursor-pointer group transition-all`}
                      onClick={() => onOpen(out)}
                    >
                       <div className="flex flex-wrap items-center gap-1.5 mb-2">
                         <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${out.type === 'analysis' ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50'}`}>
                           {out.type.replace('-', ' ')}
                         </span>
                         {needsFollowup && (
                           <span className="flex items-center gap-1 px-1 py-0.5 text-[9px] font-medium text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 rounded">
                             <AlertCircle className="w-3 h-3" /> Follow-up
                           </span>
                         )}
                       </div>
                       <h4 className="text-sm font-medium text-slate-800 dark:text-slate-200 leading-tight mb-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                         {out.title}
                       </h4>
                       
                       {td && (
                         <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1 mb-3">
                           {td.company && (
                             <div className="flex items-center gap-1.5 truncate">
                               <Building2 className="w-3 h-3 shrink-0" />
                               <span className="truncate">{td.company}</span>
                             </div>
                           )}
                           {td.targetLocation && (
                             <div className="flex items-center gap-1.5 truncate">
                               <MapPin className="w-3 h-3 shrink-0" />
                               <span className="truncate">{td.targetLocation}</span>
                             </div>
                           )}
                         </div>
                       )}

                       <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800/50">
                          <div className="text-[10px] text-slate-400 flex items-center gap-1 shrink-0 whitespace-nowrap overflow-hidden pr-1">
                            {out.created && <><Clock className="w-3 h-3 shrink-0" /> <span className="truncate">{new Date(out.created).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></>}
                          </div>
                          
                          <div className="flex items-center gap-0.5 justify-end shrink-0">
                            {out.data?.markdown && (
                              <button
                                onClick={(e) => handleDownload(e, out)}
                                className="p-1 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer text-xs"
                                title="Download as Markdown"
                                aria-label="Download as Markdown"
                              >
                                <Download className="w-3.5 h-3.5" />
                              </button>
                            )}
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(out.id);
                              }}
                              className="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                              title="Delete saved item"
                              aria-label="Delete saved item"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                     </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
